import React, { useState, useEffect, useRef } from 'react';
import { format, subDays, addDays, parseISO } from 'date-fns';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import { DeploymentPlayer } from 'cccisd-laravel-assignment';
import axios from 'cccisd-axios';
import IconLeft from 'cccisd-icons/arrow-left3';
import IconRight from 'cccisd-icons/arrow-right3';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import progressQuery from './progress.graphql';
import assignmentIdQuery from './assignmentId.graphql';
import style from './style.css';

export const DAILY_TRACKING_DEPLOYMENT_HANDLE = 'dailyTrackingRepeatable';

const Appdefs = window.cccisd.appDefs;
const Boilerplate = window.cccisd.boilerplate;
const Fortress = window.cccisd.fortress;

const DailyTracking = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [assignmentId, setAssignmentId] = useState(null);
    const [familyMembers, setFamilyMembers] = useState([]);
    const [dateToDeploymentMap, setDateToDeploymentMap] = useState({});
    const [dateToApMap, setDateToApMap] = useState({});
    const [familyHighestDeploymentId, setFamilyHighestDeploymentId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currPlaying, setCurrPlaying] = useState(null);
    const [parentDeploymentId, setParentDeploymentId] = useState(null);
    const [playPawnId, setPlayPawnId] = useState(0);
    const [err, setErr] = useState(null);
    const surveyModalRef = useRef(null);

    async function getDeploymentData(wasPlaying) {
        if (!Array.isArray(familyMembers) || !familyMembers.length) {
            return;
        }

        setIsLoading(true);

        const deploymentsResp = await client.query({
            query: progressQuery,
            variables: {
                assignmentId,
                pawnIds: familyMembers.reduce((all, curr) => {
                    all.push(curr.pawn.pawnId);
                    return all;
                }, []), // add Pawn IDs of all family members here
            },
            fetchPolicy: 'network-only',
        });

        const allDeployments = _get(deploymentsResp, 'data.deployments.deploymentList', []) || [];

        let highestDeploymentId = 0;
        let dateApMap = familyMembers.reduce((all, curr) => {
            all[curr.pawn.pawnId] = [];
            return all;
        }, {});

        // all family members will be on the same deployment ID (timepoint) per calendar date
        let dateDepMap = allDeployments.reduce((mapping, deployment) => {
            if (!deployment.parentDeploymentId || !deployment.timepoint) {
                if (deployment.deploymentHandle === DAILY_TRACKING_DEPLOYMENT_HANDLE) {
                    setParentDeploymentId(deployment.deploymentId);
                }
                return mapping;
            }

            const familyApList = _get(deployment, 'assignmentProgressList', []) || [];

            // n^2 runtime is slow, but only a max of 4-ish for family AP, so should be more like "4n" ?? *shrugs*
            const dateForSomeone = familyApList.reduce((date, ap) => {
                dateApMap[ap.pawnId].push({
                    completed: ap.completed,
                    dailyTrackingDate: ap.devTags.dailyTrackingDate,
                    dailyTrackingWeigh: ap.devTags.dailyTrackingWeigh,
                    dailyTrackingWeighAmount: ap.devTags.dailyTrackingWeighAmount,
                });

                // if it's not complete, ignore this result since eraseMetricsIfIncomplete will just delete it below
                if (
                    wasPlaying &&
                    wasPlaying.pawnId === ap.pawnId &&
                    wasPlaying.deploymentId === deployment.deploymentId &&
                    !ap.completed
                ) {
                    return date;
                }

                const trackingDate = _get(ap, 'devTags.dailyTrackingDate');
                if (date && trackingDate && date !== trackingDate) {
                    setErr(
                        'Misconfiguration. Please consult an administrator at support@3cisd.com. ' +
                            'There are 2 family members with different tracking dates for the same deployment ID!'
                    );
                    return;
                }

                if (!date) {
                    return trackingDate;
                }

                return date;
            }, null);

            if (dateForSomeone) {
                mapping[dateForSomeone] = deployment;

                if (highestDeploymentId <= deployment.deploymentId) {
                    highestDeploymentId = deployment.deploymentId;
                }
            }

            return mapping;
        }, {});

        setFamilyHighestDeploymentId(highestDeploymentId === 0 ? null : highestDeploymentId); // convert 0 to null to avoid ambiguity on backend
        setDateToDeploymentMap(dateDepMap);
        setDateToApMap(dateApMap);

        /* call here to prevent flow_response_set from pre-populating,
            in the event that someone closes the Modal before Saving.
            TO-DO: figure out how to do this on browser refresh or close?? */
        if (wasPlaying) {
            await eraseMetricsIfIncomplete(wasPlaying);
        }

        setIsLoading(false);
    }

    useEffect(() => {
        async function getData() {
            const assignmentIdResp = await client.query({
                query: assignmentIdQuery,
                variables: {
                    handle: DAILY_TRACKING_DEPLOYMENT_HANDLE,
                    familyId: Fortress.user.acting.group_id,
                },
                fetchPolicy: 'network-only',
            });

            const aId = _get(assignmentIdResp, 'data.deployments.deployment.assignment.assignmentId', null);
            if (!aId) {
                setErr(
                    `Misconfiguration. Please consult an administrator at support@3cisd.com. Could not get assignment ID for deployment handle "${DAILY_TRACKING_DEPLOYMENT_HANDLE}".`
                );
                setIsLoading(false);
                return;
            }

            const family = _get(assignmentIdResp, 'data.roles.anyRoleList', []) || [];
            if (!family || family.length === 0) {
                setErr(
                    `Misconfiguration. Please consult an administrator at support@3cisd.com. Could not get list of family members.`
                );
                setIsLoading(false);
                return;
            }

            family.sort((a, b) => {
                const roleA = renderFamilyMemberRole(a);
                const roleB = renderFamilyMemberRole(b);

                if (roleA !== roleB) {
                    // parents on top
                    return roleB.localeCompare(roleA);
                }

                const nameA = renderFamilyMemberName(a);
                const nameB = renderFamilyMemberName(b);
                return nameA.localeCompare(nameB);
            });

            setFamilyMembers(family);
            setAssignmentId(aId);
        }

        getData();
    }, []);

    // This should only run once on page load, after fetching assignmentId and familyMembers
    useEffect(() => {
        if (assignmentId && Array.isArray(familyMembers) && familyMembers.length > 0) {
            getDeploymentData();
        }
    }, [assignmentId, familyMembers]);

    useEffect(() => {
        if (!currPlaying || !currPlaying.pawnId || !currPlaying.deploymentId) {
            if (surveyModalRef?.current) {
                surveyModalRef.current.close();
            }
            return;
        }

        surveyModalRef.current.open();
    }, [currPlaying, surveyModalRef]);

    // `playPawnId` is only used to trigger data refetch before calculating which deployment ID to use
    useEffect(() => {
        if (playPawnId !== 0) {
            onClickPlay(familyMembers.find(member => member.pawn.pawnId === playPawnId));
            setPlayPawnId(0);
        }
    }, [playPawnId, familyMembers]);

    async function eraseMetricsIfIncomplete(wasPlaying) {
        if (!wasPlaying?.pawnId || !wasPlaying?.pawnHash || !wasPlaying?.deploymentId) {
            return;
        }

        await axios.post(Boilerplate.route('api.app.dailyTracking.eraseMetricsIfIncomplete'), wasPlaying);
    }

    function formatDateString(dateObj) {
        if (!dateObj) {
            return null;
        }

        if (!(dateObj instanceof Date) || Number.isNaN(dateObj.valueOf())) {
            return null;
        }

        return format(dateObj, 'EEEE, MMMM d, yyyy');
    }

    function formatDateForMetrics(dateObj) {
        if (!dateObj) {
            return null;
        }

        if (!(dateObj instanceof Date) || Number.isNaN(dateObj.valueOf())) {
            return null;
        }

        return format(dateObj, 'yyyy-MM-dd');
    }

    function renderFamilyMemberName(member) {
        if (!member?.user) {
            return;
        }

        if (
            member.user.firstName &&
            !familyMembers.find(
                someoneElse =>
                    someoneElse.user.firstName === member.user.firstName &&
                    someoneElse.pawn.pawnId !== member.pawn.pawnId
            )
        ) {
            return member.user.firstName;
        }

        if (
            member.user.firstName &&
            member.user.lastName &&
            !familyMembers.find(
                someoneElse =>
                    someoneElse.user.lastName === member.user.lastName && someoneElse.pawn.pawnId !== member.pawn.pawnId
            )
        ) {
            return `${member.user.firstName} ${member.user.lastName}`;
        }

        if (member.user.firstName) {
            return `${member.user.firstName} (${member.user.username})`;
        }

        return member.user.username;
    }

    function renderFamilyMemberRole(member) {
        if (!member?.role?.handle) {
            return '';
        }

        let display = '';
        try {
            display = Appdefs.pawn.roles.find(r => r.handle === member.role.handle).label;
            display = display.replace(/TFH/g, '').trim();
        } catch (e) {
            // meh just don't show anything
        }

        return display;
    }

    async function onClickPlay(member) {
        if (!parentDeploymentId) {
            setErr('Misconfiguration. Please contact support@3cisd.com. No parent data collection wave could be found');
        }
        const currDeploymentData = dateToDeploymentMap[formatDateForMetrics(selectedDate)];

        let deploymentId = null;
        const pawnId = member.pawn.pawnId;

        // means no family member has started yet for the given calendar date
        if (!currDeploymentData) {
            const resp = await axios.get(
                Boilerplate.route('api.assignmentDeployment.repeated', {
                    deploymentId: parentDeploymentId,
                    pawnId,
                    hash: member.pawn.pawnHash,
                }),
                {
                    params: {
                        isPawnHash: true,
                        startFromDeploymentId: familyHighestDeploymentId,
                    },
                }
            );

            deploymentId = _get(resp, 'data.data.deploymentId', -1);
        } else {
            deploymentId = currDeploymentData.deploymentId;
        }

        setCurrPlaying({
            deploymentId,
            pawnId: member.pawn.pawnId,
            pawnHash: member.pawn.pawnHash,
        });
    }

    function renderStartButton(member, btnText = 'Start') {
        return (
            <button
                className={`btn btn-primary ${btnText === 'Edit' ? style.buttonEdit : style.buttonStart} ${
                    style.startSurveyButton
                }`}
                type="button"
                onClick={async e => {
                    // get up-to-date in case another family member has separate browser tab open (familyHighestDeploymentId needs to be accurate)
                    await getDeploymentData();
                    await setPlayPawnId(member.pawn.pawnId);
                }}
            >
                {btnText}
            </button>
        );
    }

    function renderFamilyButton(member) {
        const thisDeployment = dateToDeploymentMap[formatDateForMetrics(selectedDate)];
        if (!thisDeployment) {
            return renderStartButton(member, 'Start');
        }

        const apList = dateToDeploymentMap[formatDateForMetrics(selectedDate)].assignmentProgressList;
        const memberProgress = apList.find(ap => ap.pawnId === member.pawn.pawnId);
        if (!memberProgress) {
            return renderStartButton(member, 'Start');
        }

        if (memberProgress.started && !memberProgress.completed) {
            return renderStartButton(member, 'Resume');
        }

        return renderStartButton(member, 'Edit');
    }

    if (err) {
        return (
            <div className="alert alert-warning">
                <p className="mb-0">{err}</p>
            </div>
        );
    }

    return (
        <Loader loading={isLoading} hideChildren={false}>
            <div className={style.page}>
                <Modal
                    trigger={null}
                    ref={surveyModalRef}
                    afterClose={async () => {
                        const wasPlaying = { ...currPlaying };
                        setCurrPlaying(null); // this unmounts Flow, which saves flow_response_set

                        if (wasPlaying?.pawnId) {
                            await getDeploymentData(wasPlaying); // this refetches family data and also wipes flow_response_set if not complete
                        }
                    }}
                    title={
                        <span>
                            <p className="mb-0 fs-1">
                                {renderFamilyMemberName(
                                    familyMembers.find(
                                        mem => !!currPlaying?.pawnId && mem.pawn.pawnId === currPlaying.pawnId
                                    )
                                )}
                            </p>
                            <p className="mb-0 fs-6 fst-italic fw-light">{formatDateString(selectedDate)}</p>

                            {/* Yikes! We need a better way to hide the Close button, but <style> tag is just too convenient */}
                            <style>{`
                                .modal-header button.btn-close {
                                  display: none;
                                }
                            `}</style>
                        </span>
                    }
                    render={() => {
                        let content;
                        if (!currPlaying?.deploymentId || !currPlaying?.pawnId || !currPlaying?.pawnHash) {
                            content = <Loader loading type="inline" />;
                        } else {
                            const allAps = dateToApMap[currPlaying.pawnId];
                            allAps.sort((a, b) => {
                                if (!a?.dailyTrackingDate) {
                                    return 1;
                                }

                                if (!b?.dailyTrackingDate) {
                                    return -1;
                                }

                                return new Date(b.dailyTrackingDate) - new Date(a.dailyTrackingDate);
                            });

                            const lastWeighAp = allAps.find(ap => {
                                return (
                                    ap?.dailyTrackingWeigh &&
                                    ap?.dailyTrackingDate &&
                                    parseInt(ap.dailyTrackingWeigh, 10) === 1 &&
                                    ap.dailyTrackingDate.localeCompare(formatDateForMetrics(selectedDate)) === -1 // formatted YYYY-MM-DD, so can rely on ABC order
                                );
                            });

                            content = (
                                <DeploymentPlayer
                                    deploymentId={currPlaying.deploymentId}
                                    pawnId={currPlaying.pawnId}
                                    pawnHash={currPlaying.pawnHash}
                                    onComplete={() => surveyModalRef.current.close()}
                                    flowProps={{
                                        variables: {
                                            dailyTrackingDate: formatDateForMetrics(selectedDate),
                                            dailyTrackingCompletedById: Fortress.user.acting?.role_data?.studyId,
                                            dailyTrackingCompletedByUsername: Fortress.user.acting.user.username,
                                            greenGoal: 6,
                                            redGoal: 2,
                                            calorieGoalMin: 1750,
                                            calorieGoalMax: 1950,
                                            activityGoal: 45,
                                            lastWeighAmount: lastWeighAp?.lastWeightAmount,
                                            lastWeightDate: lastWeighAp?.dailyTrackingDate
                                                ? formatDateString(parseISO(lastWeighAp.dailyTrackingDate))
                                                : '',
                                        },
                                    }}
                                    disableLayout
                                />
                            );
                        }

                        return (
                            <div className={style.modalContent} data-cccisd-survey-area>
                                {content}
                            </div>
                        );
                    }}
                />
                <div className={style.datePicker}>
                    <button
                        type="button"
                        onClick={() => {
                            setSelectedDate(subDays(selectedDate, 1));
                        }}
                    >
                        <IconLeft />
                    </button>
                    <span className={style.dateDisplay}>{formatDateString(selectedDate)}</span>
                    <button
                        type="button"
                        onClick={() => {
                            setSelectedDate(addDays(selectedDate, 1));
                        }}
                        disabled={formatDateString(selectedDate) === formatDateString(new Date())}
                    >
                        <IconRight />
                    </button>
                </div>

                <table className={'table table-bordered ' + style.table}>
                    <tbody>
                        {familyMembers
                            .filter(member => {
                                // Parents see everyone in the family; Children only see their own
                                if (Fortress.user.acting.role.handle === 'tfhChild') {
                                    return member.pawn.pawnId === Fortress.user.acting.id;
                                }
                                return true;
                            })
                            .map(member => {
                                return (
                                    <tr key={member.pawn.pawnId}>
                                        <td>
                                            <span className="d-block fs-4">{renderFamilyMemberName(member)}</span>
                                            <span className={'d-block fst-italic fw-light ' + style.roleName}>
                                                {renderFamilyMemberRole(member)}
                                            </span>
                                        </td>
                                        <td>{renderFamilyButton(member)}</td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>
        </Loader>
    );
};

export default DailyTracking;
